import { useCallback, useEffect, useRef } from 'react';
import bridge from '@vkontakte/vk-bridge';

const API_URL = 'https://freegptcontact.ru';

async function chatAdRequest(data) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 4000);

  try {
    const response = await fetch(API_URL + '/chat_auto_ad', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'vk-sign': window.location.search
      },
      body: JSON.stringify(data),
      signal: controller.signal
    });

    if (!response.ok) {
      throw new Error('HTTP_' + response.status);
    }

    const result = await response.json();

    if (!result || typeof result !== 'object') {
      throw new Error('INVALID_RESPONSE');
    }

    return result;
  } finally {
    clearTimeout(timer);
  }
}

async function supportsNativeAds() {
  let timer;

  try {
    return await Promise.race([
      bridge.supportsAsync('VKWebAppShowNativeAds'),
      new Promise(resolve => {
        timer = setTimeout(() => resolve(false), 2000);
      })
    ]);
  } finally {
    clearTimeout(timer);
  }
}

export default function useChatAutoAds(userId, fullscreenAdRef) {
  const currentUserRef = useRef(userId);
  currentUserRef.current = userId;

  useEffect(() => {
    if (!userId) return;

    void chatAdRequest({ action: 'init' }).catch(() => {
      // Статус рекламы не должен мешать запуску приложения.
    });
  }, [userId]);

  return useCallback(async () => {
    if (
      !userId ||
      fullscreenAdRef.current ||
      document.visibilityState !== 'visible'
    ) {
      return;
    }

    fullscreenAdRef.current = true;

    try {
      const supported = await supportsNativeAds();

      if (!supported) return;

      if (
        currentUserRef.current !== userId ||
        document.visibilityState !== 'visible'
      ) {
        return;
      }

      const decision = await chatAdRequest({ action: 'prepare' });

      if (
        decision.show !== true ||
        typeof decision.ticket !== 'string' ||
        !/^[a-f0-9]{32}$/.test(decision.ticket)
      ) {
        return;
      }

      let outcome = 'cancelled';
      let errorCode = '';

      if (
        currentUserRef.current === userId &&
        document.visibilityState === 'visible'
      ) {
        try {
          const result = await bridge.send('VKWebAppShowNativeAds', {
            ad_format: 'interstitial'
          });

          outcome = result?.result === true ? 'shown' : 'failed';
        } catch (error) {
          errorCode = String(
            error?.error_data?.error_code ?? ''
          ).replace(/[^A-Za-z0-9_.:-]/g, '').slice(0, 64);

          outcome = errorCode === '20' ? 'no_ads' : 'failed';

          console.warn('Автоматическая реклама не показана:', {
            error_type: error?.error_type,
            error_code: errorCode
          });
        }
      }

      try {
        await chatAdRequest({
          action: 'finish',
          ticket: decision.ticket,
          outcome,
          error_code: errorCode
        });
      } catch {
        // Серверная бронь истечёт сама. Запрос в чат продолжается.
      }
    } catch {
      // Ошибка подготовки рекламы не отменяет отправку сообщения.
    } finally {
      fullscreenAdRef.current = false;
    }
  }, [userId, fullscreenAdRef]);
}